import { useEffect, useRef } from 'react'
import Player from '@vimeo/player'
import { useLesson } from 'querys/lesson'
import { useLessonCompleted, useVideoProgress } from 'mutations/lesson'
import { useRouter } from 'next/router'
import useViewportHeight from 'hooks/use-viewport-height'
import * as gtag from 'utils/gtag'

export function verifyUrl(url) {
  if (!url) return undefined
  if (!isNaN(url)) {
    return url
  }
  const str = url.split('https://player.vimeo.com/video/')
  return str[1]
}

async function setVolume(player) {
  if (typeof navigator === 'undefined') return null
  const volume = localStorage.getItem('volume') || 1
  switch (navigator.platform) {
    case 'iPad':
    case 'iPhone':
      try {
        await player.setVolume(volume)
      } catch (err) {
        console.log(err)
      }
      break
    default:
      return player.setVolume(volume)
  }
}

export default function useSaveProgress({ progress_time, url_vimeo }) {
  const router = useRouter()
  const viewportHeight = useViewportHeight()
  const lessonCompleted = useLessonCompleted()
  const {
    data: {
      course: { slug },
      lesson_navigation: { next },
      completed,
      total_duration,
      title,
    },
  } = useLesson()

  const videoProgress = useVideoProgress()
  const iframe = useRef(null)
  useEffect(() => {
    if (iframe.current && url_vimeo) {
      const player = new Player(iframe.current, { pip: true })
      const speed = localStorage.getItem('speed')
      const quality = localStorage.getItem('quality')
      let interval
      let lesson_completed = completed
      if (total_duration - progress_time < 10) {
        player.setCurrentTime(1)
      } else {
        player.setCurrentTime(Number(progress_time))
      }
      if (speed) {
        player.setPlaybackRate(speed)
      }
      if (quality) {
        player.setQuality(quality)
      }
      player.on('play', () => {
        setVolume(player)
        interval = window.setInterval(async () => {
          const position = await player.getCurrentTime()
          gtag.event({
            action: 'video_progress',
            category: 'lesson',
            label: title,
            value: position,
          })
          if (!lesson_completed && (total_duration * 0.80) < position) {
            lessonCompleted.mutate(router.query.lesson_slug)
            lesson_completed = true
            return
          }
          videoProgress.mutate(position)
        }, 10000)
      })
      player.on('pause', () => {
        window.clearInterval(interval)
      })
      player.on('seeking', () => {
        window.clearInterval(interval)
      })
      player.on('playbackratechange', ({ playbackRate }) => {
        localStorage.setItem('speed', playbackRate)
      })
      player.on('qualitychange', ({ quality: videoQuality }) => {
        localStorage.setItem('quality', videoQuality)
      })
      player.on('volumechange', ({ volume }) => {
        localStorage.setItem('volume', volume)
      })
      player.on('ended', () => {
        window.clearInterval(interval)
        videoProgress.mutate(0)
        const actualScroll = document.documentElement.scrollTop || document.body.scrollTop
        if (actualScroll > viewportHeight * 0.33) return
        const texteareas = document.querySelectorAll('textarea')
        if (!lesson_completed) {
          lessonCompleted.mutate(router.query.lesson_slug)
          lesson_completed = true
        }
        const writing = Array.from(texteareas).find((area) => area.value !== '')
        if (writing) return
        if (next?.slug) {
          router.push(
            '/aprender/[course]/[lesson_slug]',
            `/aprender/${slug}/${next?.slug}`,
          )
        } else {
          router.push({
            pathname: '/aprender/[course]',
            query: { course: slug, review: 'open' },
          })
        }
      })
      return () => {
        player.off('play')
        player.off('pause')
        player.off('ended')
        player.off('seeking')
        player.off('playbackratechange')
        player.off('volumechange')
        player.off('qualitychange')
        window.clearInterval(interval)
      }
    }
  }, [url_vimeo])

  return { iframe }
}
